import {useCompanyTariffGuard} from '../../../../../../../modules/company-tariff';
import {closestCenter, DndContext, DragEndEvent, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {arrayMove, SortableContext, verticalListSortingStrategy} from '@dnd-kit/sortable';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {Button, IconButton, Stepper as MuiStepper, Stack, Typography} from '@mui/material';
import {FC, useContext, useState} from 'react';
import {useFieldArray} from 'react-hook-form';
import {useStepsFormContext} from '../../contexts';
import {downloadJsonFile, getNewStep, importJsonFile} from '../../helpers';
import {useStepperStore} from '../../store/useStepperStore';
import {useStepStore} from '../../store/useStepStore';
import {hasNewWorkStageItems} from '../../helpers/has-new-work-stage-items';
import {useStepSettingsStore} from '../../store/useStepSettingsStore';
import {STYLES} from './styles';
import {SortableStep} from './sortable-step';
import {ToastHelpers} from '../../../../../../../shared/helpers/toastHelpers';
import {EventBusContext} from '../../../../../../../providers/EventBus';
import {WorkStagesService} from '../../../../../../../api/services/workStages.service';
import {
  StepWithNullableId,
  WorkStagesDataWithDeviceTypeCode,
  WorkStagesServiceData,
} from '../../../../../../../shared/types/form.types';
import {useParams} from 'react-router-dom';
import {deviceTypeDictQueryByCodeFamily} from '../../../../../../../store/deviceTypes/selectors';
import {useRecoilStateLoadable} from 'recoil';
import {useEventBus} from '../../../../../../../hooks/useEventBus';
import {LoadingButton} from '@mui/lab';

const removeTempIds = (data: WorkStagesServiceData) => {
  const newData = {
    ...data,
    steps: data.steps.reduce((acc, curr) => {
      acc.push({
        ...curr,
        id: curr.id.includes('temp_') ? null : curr.id,
        fieldGroups: curr.fieldGroups.map((item) => ({...item, id: item.id.includes('temp_') ? null : item.id})),
      });

      return acc;
    }, [] as StepWithNullableId[]),
  };

  return newData;
};

export const Stepper: FC = () => {
  const {checkTariff, tariffLoading} = useCompanyTariffGuard();
  const {selectedStepId, selectStep, goToPrevStep} = useStepperStore();
  const {control, watch, setValue, getValues, reset} = useStepsFormContext();
  const {setCanTitleEdit} = useStepSettingsStore();
  const {append} = useFieldArray({control, name: 'steps'});
  const fields = watch('steps');
  const savedSteps = useStepStore((state) => state.steps);
  const hasNewItems = hasNewWorkStageItems(fields, savedSteps);
  const bus = useEventBus();
  const [isLoading, setIsLoading] = useState(false);

  const {id} = useParams();
  const [{contents}] = useRecoilStateLoadable(deviceTypeDictQueryByCodeFamily(id));
  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({active, over}: DragEndEvent) => {
    if (active.id !== over?.id) {
      const oldIndex = fields.findIndex((step) => step.id === active.id);
      const newIndex = fields.findIndex((step) => step.id === over?.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        setValue('steps', arrayMove(fields, oldIndex, newIndex));
      }
    }
  };

  const handleAddStep = () => {
    if (!checkTariff()) return;
    const step = getNewStep();
    append(step);
    selectStep(step.id);
    setCanTitleEdit(step.id, true);
  };

  const handleImportSchema = () => {
    if (!checkTariff()) return;
    importJsonFile({
      onSuccess: reset,
      onError: ToastHelpers.error,
    });
  };

  const handleExportSchema = () => {
    downloadJsonFile({
      ...removeTempIds(getValues()),
      deviceTypeCode: contents.code,
    } as WorkStagesDataWithDeviceTypeCode);
  };

  const handleSave = async () => {
    if (hasNewItems && !checkTariff()) return;
    setIsLoading(true);
    const serviceData = {
      ...removeTempIds(getValues()),
      deviceTypeCode: contents.code,
    };
    try {
      await WorkStagesService.createServicePath(serviceData as WorkStagesDataWithDeviceTypeCode);
      ToastHelpers.success('Данные успешно сохранены');
    } catch (err: any) {
      bus?.emit('error', [err]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack gap="64px" justifyContent="space-between">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={fields.map((field) => field.id)} strategy={verticalListSortingStrategy}>
          <MuiStepper orientation="vertical" sx={STYLES.stepper}>
            {fields.map((field, index) => (
              <SortableStep
                key={field.id}
                id={field.id}
                active={field.id === selectedStepId}
                index={index}
                title={field.title}
                onClick={() => selectStep(field.id)}
              />
            ))}
          </MuiStepper>
        </SortableContext>
      </DndContext>

      <Stack direction="row" gap={1} alignItems="center" justifyContent="flex-end" px="20px">
        {!!fields.length && (
          <IconButton onClick={goToPrevStep} sx={{width: '38px'}}>
            <ArrowBackIcon sx={STYLES.icon} />
          </IconButton>
        )}
        <Button sx={STYLES.button} fullWidth variant="contained" onClick={handleAddStep} disabled={tariffLoading}>
          Добавить шаг &nbsp; <ArrowForwardIcon />
        </Button>
      </Stack>

      <Stack direction="row" gap="14px" justifyContent="flex-end" mt="-30px" pb="22px">
        <Button
          variant="outlined"
          sx={{borderColor: '#007DFF', p: '6px 12px', borderRadius: '6px'}}
          onClick={handleImportSchema}
          disabled={tariffLoading}
        >
          <Typography sx={STYLES.buttonSecondaryTitle}>Импорт</Typography>
        </Button>

        <Button
          variant="outlined"
          color="success"
          sx={{borderColor: '#2CC378', p: '6px 12px', borderRadius: '6px'}}
          onClick={handleExportSchema}
        >
          <Typography sx={STYLES.buttonSecondaryTitle}>Экспорт</Typography>
        </Button>

        <LoadingButton
          loading={isLoading}
          variant="contained"
          color="success"
          sx={STYLES.buttonSave}
          onClick={handleSave}
          disabled={hasNewItems && tariffLoading}
        >
          <Typography sx={STYLES.buttonSaveTitle}>Сохранить</Typography>
        </LoadingButton>
      </Stack>
    </Stack>
  );
};
